import { Button, CheckLineIcon, Modal } from "@relex-solutions/fragments"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDebounce } from "use-debounce"
import { useGetCustomers } from "@/domains/customers/hooks/useGetCustomers"
import {
  SearchInputWithOptions,
  type SearchInputWithOptionsOption
} from "@/shared/components/SearchInputWithOptions/SearchInputWithOptions"
import styles from "./EditParentModal.module.css"

interface EditParentModalProps {
  open: boolean
  currentParent?: {
    id?: string
    name?: string
  }
  nodeName: string
  onClose: () => void
  onConfirm: (newParentId: string | null) => void
  isLoading?: boolean
}

interface ModalState {
  userSelectedParentId: string | null
  inputValue: string
  showResults: boolean
  hasUserModifiedInput: boolean
  hasSelectedFromList: boolean
}

const DEBOUNCE_DELAY = 300
const INITIAL_STATE: ModalState = {
  userSelectedParentId: null,
  inputValue: "",
  showResults: false,
  hasUserModifiedInput: false,
  hasSelectedFromList: false
}

export function EditParentModal({
  open,
  currentParent,
  nodeName,
  onClose,
  onConfirm,
  isLoading = false
}: EditParentModalProps) {
  const { t } = useTranslation()
  const [state, setState] = useState<ModalState>(INITIAL_STATE)

  const displayValue = useMemo(() => {
    if (state.hasUserModifiedInput) {
      return state.inputValue
    }
    return currentParent?.name || ""
  }, [state.hasUserModifiedInput, state.inputValue, currentParent?.name])

  const selectedParentId = useMemo(() => {
    if (state.hasUserModifiedInput && state.userSelectedParentId !== null) {
      return state.userSelectedParentId
    }
    return currentParent?.id || null
  }, [state.hasUserModifiedInput, state.userSelectedParentId, currentParent?.id])

  const [debouncedInputValue] = useDebounce(displayValue, DEBOUNCE_DELAY)

  const customersQuery = useGetCustomers({
    page: 0,
    pageSize: 15,
    searchTerm: debouncedInputValue,
    sortBy: "name",
    sortOrder: "ASC",
    salesforceId: "",
    sdmEmail: "",
    relationshipType: "",
    relationshipSubType: "",
    enabled: debouncedInputValue.length > 0 && state.hasUserModifiedInput && !state.hasSelectedFromList
  })

  const customerOptions = useMemo(() => {
    if (state.inputValue.length === 0) {
      return []
    }

    const searchResults = customersQuery.data?.data || []

    return searchResults.map(({ id, name }) => ({
      id,
      label: name,
      disabled: id === currentParent?.id,
      selected: id === selectedParentId,
      rightIcon: id === currentParent?.id ? <CheckLineIcon /> : null
    }))
  }, [customersQuery.data, currentParent?.id, selectedParentId, state.inputValue])

  const resetState = () => setState(INITIAL_STATE)

  const handleCancel = () => {
    resetState()
    onClose()
  }

  const handleConfirm = () => {
    onConfirm(selectedParentId)
  }

  const handleInputChange = (value: string) => {
    setState((prev) => ({
      ...prev,
      inputValue: value,
      showResults: value.length > 0,
      hasUserModifiedInput: true,
      hasSelectedFromList: false,
      userSelectedParentId: null
    }))
  }

  const handleOptionSelect = ({ id, label }: SearchInputWithOptionsOption) => {
    setState((prev) => ({
      ...prev,
      userSelectedParentId: id,
      inputValue: label,
      showResults: false,
      hasUserModifiedInput: true,
      hasSelectedFromList: true
    }))
  }

  const handleClearInput = () => {
    setState((prev) => ({
      ...prev,
      inputValue: "",
      userSelectedParentId: null,
      showResults: false,
      hasUserModifiedInput: true,
      hasSelectedFromList: false
    }))
  }

  const isConfirmDisabled = !selectedParentId || isLoading || selectedParentId === currentParent?.id
  const showResults = state.hasUserModifiedInput && state.showResults && !customersQuery.isFetching

  return (
    <Modal
      open={open}
      hasCloseIcon
      closeOnOverlayClick
      title={t("visualization.nodePropertiesDisplay.editParentTitle", { nodeName })}
      onClose={handleCancel}
      maxWidth="medium"
      footer={
        <div className={styles.footer}>
          <Button variant="secondary" onClick={handleCancel}>
            {t("general.cancel")}
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={isConfirmDisabled}>
            {t("general.save")}
          </Button>
        </div>
      }
    >
      <SearchInputWithOptions
        value={displayValue}
        placeholder={t("visualization.nodePropertiesDisplay.searchParentPlaceholder")}
        label={t("visualization.nodePropertiesDisplay.parent")}
        clearAriaLabel={t("general.clear")}
        disabled={isLoading}
        loading={customersQuery.isFetching}
        containerClass={styles.container}
        onChange={handleInputChange}
        onClear={displayValue.length > 0 ? handleClearInput : undefined}
        showResults={showResults}
        options={customerOptions}
        resultsAriaLabel={t("visualization.nodePropertiesDisplay.searchResults")}
        onSelect={handleOptionSelect}
      />
    </Modal>
  )
}
